var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { MapPin } from '@homeexchange/design';
import React, { useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import i18n from '../../../i18n';
import { useSelector } from 'react-redux';
import styles from './searchMap.module.scss';
import { useDevice } from '@homeexchange/shared';
import { Legends } from './legends';
import clsx from 'clsx';
import { HomeCard } from '../results-ts/homecard';
import useFavoriteModals from '../../../hooks/useFavoriteModals';
import { MapGoogle } from '@homeexchange/design';
import Analytics from '../../../utils/analytics';
export var SearchMap = React.forwardRef(function (_a, ref) {
    var results = _a.results, additionalResults = _a.additionalResults, initialBounds = _a.initialBounds, onBoundsChange = _a.onBoundsChange, loading = _a.loading, hoveredHomeId = _a.hoveredHomeId, user = _a.user, isLogged = _a.isLogged, searchFilters = _a.searchFilters;
    var local = i18n.language;
    var mapRef = useRef(null);
    var popupRef = useRef(null);
    /** True only after a user-initiated gesture (mouse/touch/wheel/keyboard); programmatic flyTo never sets it. */
    var userInteracting = useRef(false);
    var _b = useState(null), hoveredMarkerId = _b[0], setHoveredMarkerId = _b[1];
    var homeFavorites = useSelector(function (state) { return state.homeFavorites || []; });
    var placeId = useSelector(function (state) { var _a, _b; return (_b = (_a = state.search) === null || _a === void 0 ? void 0 : _a.main) === null || _b === void 0 ? void 0 : _b.placeId; });
    var bounds = useSelector(function (state) { var _a, _b; return (_b = (_a = state.search) === null || _a === void 0 ? void 0 : _a.main) === null || _b === void 0 ? void 0 : _b.bounds; });
    var quickAddResetKey = "".concat(placeId, "-").concat(JSON.stringify(bounds));
    var _c = useState(null), selectedHomeId = _c[0], setSelectedHomeId = _c[1];
    var markerRefs = useRef(new Map());
    var _d = useState(false), isClosing = _d[0], setIsClosing = _d[1];
    var _e = useState(0), animationKey = _e[0], setAnimationKey = _e[1];
    /** Ref avoids stale closure: useCallback must not read first-move from a frozen render. */
    var skipNextBoundsFromMoveEnd = useRef(true);
    var markUserInteraction = useCallback(function () {
        userInteracting.current = true;
    }, []);
    var isMobile = useDevice().isMobile;
    var ANIMATION_DURATION = 200;
    var _f = useFavoriteModals({
        quickAdd: true,
        quickAddResetKey: quickAddResetKey,
        trackingArea: 'search_map'
    }), handleFavoriteClick = _f.handleFavoriteClick, handleRemoveFavorite = _f.handleRemoveFavorite, handleMoveFavorite = _f.handleMoveFavorite, modals = _f.modals;
    var handleToggleFavorite = useCallback(function (homeId, prefillName) {
        handleFavoriteClick(homeId, prefillName);
    }, [handleFavoriteClick]);
    useEffect(function () {
        setHoveredMarkerId(hoveredHomeId);
    }, [hoveredHomeId]);
    var handleClosePopup = function () {
        setIsClosing(true);
        if (isMobile) {
            setTimeout(function () {
                setSelectedHomeId(null);
                setIsClosing(false);
            }, ANIMATION_DURATION);
        }
        else {
            setSelectedHomeId(null);
            setIsClosing(false);
        }
    };
    var handleMarkerClick = function (homeId) {
        if (selectedHomeId) {
            if (selectedHomeId !== homeId) {
                setIsClosing(true);
                if (isMobile) {
                    setTimeout(function () {
                        setSelectedHomeId(homeId);
                        setIsClosing(false);
                        setAnimationKey(function (prev) { return prev + 1; });
                    }, ANIMATION_DURATION);
                }
                else {
                    setSelectedHomeId(homeId);
                    setIsClosing(false);
                }
            }
            else {
                handleClosePopup();
            }
        }
        else {
            setSelectedHomeId(homeId);
            setAnimationKey(function (prev) { return prev + 1; });
        }
    };
    useEffect(function () {
        var handleOutsideClick = function (e) {
            if (popupRef.current && popupRef.current.contains(e.target)) {
                return;
            }
            var clickedOnMarker = false;
            markerRefs.current.forEach(function (markerEl) {
                if (markerEl.contains(e.target)) {
                    clickedOnMarker = true;
                }
            });
            if (!clickedOnMarker) {
                setHoveredMarkerId(null);
                if (selectedHomeId) {
                    handleClosePopup();
                }
            }
        };
        document.addEventListener('click', handleOutsideClick);
        return function () {
            document.removeEventListener('click', handleOutsideClick);
        };
    }, [selectedHomeId]);
    var createMarkers = function (results, isUnavailable) {
        var _a;
        if (isUnavailable === void 0) { isUnavailable = false; }
        return ((_a = results === null || results === void 0 ? void 0 : results.map(function (result) {
            var _a;
            var homeId = result.attributes.homeId;
            var isFavorite = homeFavorites.includes(homeId);
            var isHovered = hoveredMarkerId === homeId;
            var isSelected = selectedHomeId === homeId;
            var pinVariant = isHovered || isSelected ? 'selected' : isUnavailable ? 'unavailable' : 'default';
            var pinDecoration = isFavorite ? 'favorite' : undefined;
            var getTrackingId = function () {
                if (isUnavailable) {
                    if (isFavorite) {
                        return 'click_pin_favorite_unavailable';
                    }
                    else {
                        return 'click_pin_unavailable';
                    }
                }
                else {
                    if (isFavorite) {
                        return 'click_pin_favorite_available';
                    }
                    else {
                        return 'click_pin_available';
                    }
                }
            };
            var getPositionOrder = function () {
                switch (true) {
                    case isHovered || isSelected:
                        return 10;
                    case isUnavailable && !isFavorite:
                        return 1;
                    case isUnavailable && isFavorite:
                        return 2;
                    case !isUnavailable && !isFavorite:
                        return 3;
                    case !isUnavailable && isFavorite:
                        return 4;
                    default:
                        return -1;
                }
            };
            return {
                latitude: result.attributes.location.coordinates.latitude,
                longitude: result.attributes.location.coordinates.longitude,
                indexPosition: getPositionOrder(),
                marker: (React.createElement("div", { ref: function (el) {
                        if (el)
                            markerRefs.current.set(homeId, el);
                        else
                            markerRefs.current.delete(homeId);
                    }, className: clsx(styles['marker-container'], (_a = {},
                        _a[styles['marker-hovered']] = isHovered || isSelected,
                        _a)), onMouseEnter: function () { return setHoveredMarkerId(homeId); }, onMouseLeave: function () { return setHoveredMarkerId(null); }, onClick: function () {
                        var eventData = {
                            action: 'click',
                            text: getTrackingId(),
                            area: 'search_page_map'
                        };
                        Analytics.trackGTM('SearchMap', { event_data: eventData });
                        handleMarkerClick(homeId);
                    } },
                    React.createElement(MapPin, { variant: pinVariant, decoration: pinDecoration }))),
                popup: (React.createElement(HomeCard, { result: result, locale: local, isLogged: isLogged, isUserCollection: user === null || user === void 0 ? void 0 : user.get('is_he_collection'), homeFavorites: homeFavorites, handleToggleFavorite: handleToggleFavorite, format: "map", onClose: handleClosePopup, user: user, searchFilters: searchFilters, hideFixedDates: results.some(function (r) { return r.attributes.homeId === result.attributes.homeId; }), trackingEvent: "map", origin: results.find(function (r) { return r.attributes.homeId === result.attributes.homeId; })
                        ? 'main_search'
                        : 'dual_search' }))
            };
        })) !== null && _a !== void 0 ? _a : []);
    };
    var markers = createMarkers(results);
    var additionalMarkers = createMarkers(additionalResults, true);
    var allMarkers = __spreadArray(__spreadArray([], markers, true), additionalMarkers, true);
    var handleMoveEnd = useCallback(function (_event, mapBounds) {
        // Skip the first moveend (initial fit / idle) so it does not fire a search bounds change.
        if (skipNextBoundsFromMoveEnd.current) {
            skipNextBoundsFromMoveEnd.current = false;
            return;
        }
        // moveend fires for both user gestures and programmatic flyTo; only the former should re-search.
        if (!userInteracting.current) {
            return;
        }
        userInteracting.current = false;
        if (mapBounds && onBoundsChange) {
            onBoundsChange({
                ne: { lat: mapBounds.getNorthEast().lat(), lon: mapBounds.getNorthEast().lng() },
                sw: { lat: mapBounds.getSouthWest().lat(), lon: mapBounds.getSouthWest().lng() }
            });
        }
    }, [onBoundsChange]);
    useImperativeHandle(ref, function () { return ({
        unzoom: function (scale) {
            var _a;
            if (scale === void 0) { scale = 1.5; }
            var map = (_a = mapRef.current) === null || _a === void 0 ? void 0 : _a.map;
            if (map) {
                var currentZoom = map.getZoom();
                if (typeof currentZoom === 'number') {
                    userInteracting.current = true;
                    map.setZoom(currentZoom / scale);
                }
            }
        },
        flyTo: function (bounds) {
            var handle = mapRef.current;
            if (handle === null || handle === void 0 ? void 0 : handle.flyToBounds) {
                handle.flyToBounds({
                    north: bounds.ne.lat,
                    south: bounds.sw.lat,
                    east: bounds.ne.lon,
                    west: bounds.sw.lon
                });
            }
        }
    }); });
    return (React.createElement(React.Fragment, null,
        loading && (React.createElement("div", { className: styles['loading-container'] },
            React.createElement("div", { className: styles['loading-dot'] }),
            React.createElement("div", { className: styles['loading-dot'] }),
            React.createElement("div", { className: styles['loading-dot'] }))),
        React.createElement(Legends, null),
        React.createElement("div", { onMouseDownCapture: markUserInteraction, onWheelCapture: markUserInteraction, onTouchStartCapture: markUserInteraction, onKeyDownCapture: markUserInteraction, style: { display: 'contents' } },
            React.createElement(MapGoogle, { ref: mapRef, apiKey: process.env.GOOGLE_MAPS_API_KEY, mapId: process.env.GOOGLE_MAPS_MAP_ID, lazyLoad: true, zoom: 11, className: styles['search-map'], markers: allMarkers.map(function (marker) { return ({
                    position: {
                        lat: marker.latitude,
                        lng: marker.longitude
                    },
                    marker: marker.marker,
                    popup: marker.popup,
                    indexPosition: marker.indexPosition
                }); }), bounds: initialBounds && {
                    north: initialBounds.ne.lat,
                    south: initialBounds.sw.lat,
                    east: initialBounds.ne.lon,
                    west: initialBounds.sw.lon
                }, onMoveEnd: handleMoveEnd, debounceEventsTime: 800, onMapLoad: function () {
                    Analytics.trackGTM('map', {
                        event_data: {
                            action: 'map_open',
                            text: 'map_open',
                            area: 'search_page'
                        }
                    });
                }, gestureHandling: "greedy" })),
        modals));
});
